import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Chip } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export function buildCompanyTree(companies) {
    const sorted = [...companies].sort((a, b) => a.path.localeCompare(b.path));
    const nodeMap = {};
    const roots = [];

    for (const company of sorted) {
        const normalizedPath = company.path.replace(/\/$/, '');
        const node = { ...company, _normalizedPath: normalizedPath, children: [] };
        nodeMap[normalizedPath] = node;

        const segments = normalizedPath.split('/').filter(Boolean);
        if (segments.length <= 1) {
            roots.push(node);
        } else {
            const parentPath = '/' + segments.slice(0, -1).join('/');
            if (nodeMap[parentPath]) {
                nodeMap[parentPath].children.push(node);
            } else {
                roots.push(node);
            }
        }
    }

    return roots;
}

function CompanyNode({ node, depth, value, onChange, readOnly }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(true);
    const hasChildren = node.children.length > 0;
    const segments = node._normalizedPath.split('/').filter(Boolean);
    const label = node.name || segments[segments.length - 1] || node._normalizedPath;
    const selected = !readOnly && value === node.path;

    const handleClick = () => {
        if (hasChildren) setOpen(o => !o);
        if (!readOnly) onChange(node.path);
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pl: depth * 3,
                    py: 0.75,
                    borderRadius: 1,
                    cursor: readOnly ? (hasChildren ? 'pointer' : 'default') : 'pointer',
                    bgcolor: selected ? 'primary.main' : 'transparent',
                    color: selected ? 'primary.contrastText' : 'inherit',
                    '&:hover': readOnly
                        ? (hasChildren ? { bgcolor: 'action.hover' } : {})
                        : { bgcolor: selected ? 'primary.dark' : 'action.hover' },
                }}
                onClick={handleClick}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', width: 20, flexShrink: 0 }}>
                    {hasChildren
                        ? (open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />)
                        : null}
                </Box>
                <BusinessIcon fontSize="small" sx={{ color: selected ? 'inherit' : 'primary.main', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={depth === 0 ? 600 : 400}>
                        {label}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {node.path}
                    </Typography>
                </Box>
                <Chip
                    label={node.active ? t('companyTreePicker.active') : t('companyTreePicker.inactive')}
                    color={node.active ? 'success' : 'default'}
                    size="small"
                    sx={{ visibility: selected ? 'hidden' : 'visible' }}
                />
            </Box>
            {hasChildren && open && node.children.map(child => (
                <CompanyNode
                    key={child.companyID}
                    node={child}
                    depth={depth + 1}
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                />
            ))}
        </Box>
    );
}

/**
 * Tree-based company picker / viewer.
 *
 * Props:
 *   companies  – flat array of company objects
 *   value      – selected company path (string); ignored when readOnly
 *   onChange   – called with the selected path; ignored when readOnly
 *   readOnly   – render as a non-interactive tree (default false)
 */
export default function CompanyTreePicker({ companies, value, onChange, readOnly = false }) {
    const { t } = useTranslation();
    const tree = buildCompanyTree(companies);

    const containerSx = readOnly
        ? { p: 0.5 }
        : { border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 300, overflowY: 'auto', p: 0.5 };

    return (
        <Box sx={containerSx}>
            {tree.length === 0
                ? <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>{t('companyTreePicker.noCompanies')}</Typography>
                : tree.map(node => (
                    <CompanyNode
                        key={node.companyID}
                        node={node}
                        depth={0}
                        value={value}
                        onChange={onChange ?? (() => {})}
                        readOnly={readOnly}
                    />
                ))
            }
        </Box>
    );
}
